import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    fetchAllEmployeeReports,
    fetchMonthlyScores,
    submitReview,
    setSelectedReport,
    clearErrors,
    clearSuccess,
} from "../../../store/slices/performanceReviewSlice";
import { format } from "date-fns";
import "./index.scss";
import ReportList from "./components/ReportList";
import ReviewForm from "./components/ReviewForm";
import MonthlyScores from "../../PerformanceReview/components/MonthlyScores";
import MonthlyScoreEmailPopup from "./components/MonthlyScoreEmailPopup";
import WeeklyReminderPopup from "./components/WeeklyReminderPopup";
import {
    HiOutlineUserGroup,
    HiExclamationCircle,
    HiCheckCircle,
} from "react-icons/hi";

const AdminPerformanceReview = () => {
    const dispatch = useDispatch();
    const { user, employees } = useSelector((state) => state.auth);
    const {
        employeeReports,
        monthlyScores,
        selectedReport,
        loading,
        error,
        reviewLoading,
        reviewError,
        reminderLoading,
        reminderSuccess,
        reminderError,
    } = useSelector((state) => state.performanceReview);

    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [activeTab, setActiveTab] = useState("reports");
    const [selectedReportId, setSelectedReportId] = useState(null);

    // Reset selected report when employee changes
    useEffect(() => {
        dispatch(setSelectedReport(null));
        setSelectedReportId(null);
    }, [selectedEmployee, dispatch]);

    // Fetch employee reports when selected
    useEffect(() => {
        if (selectedEmployee) {
            dispatch(fetchAllEmployeeReports(selectedEmployee));
            dispatch(fetchMonthlyScores(selectedEmployee));
        }
    }, [selectedEmployee, dispatch]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            dispatch(clearErrors());
            dispatch(clearSuccess());
        };
    }, [dispatch]);

    const handleEmployeeChange = (e) => {
        setSelectedEmployee(e.target.value);
        setActiveTab("reports");
    };

    const handleReportSelect = (report) => {
        setSelectedReportId(report.id);
        dispatch(setSelectedReport(report));
    };

    const handleTabSelect = (tab) => {
        setActiveTab(tab);
    };

    const handleReviewSubmit = async (reviewData) => {
        if (!selectedReport) return;

        const selectedEmployeeObj = employees.find(
            (emp) => emp.email === selectedEmployee
        );

        await dispatch(
            submitReview({
                reportId: selectedReport.id,
                reviewData,
                employeeEmail: selectedEmployee,
                employeeName: selectedEmployeeObj?.name || "Employee",
                reviewerName: user?.displayName || user?.name,
                reviewerEmail: user?.email,
            })
        );

        // Refresh employee reports after submission
        if (!reviewError) {
            dispatch(fetchAllEmployeeReports(selectedEmployee));
        }
    };

    const getEmployeeReportsList = () => {
        return employeeReports && employeeReports[selectedEmployee]
            ? employeeReports[selectedEmployee]
            : [];
    };

    return (
        <div className="admin-performance-review">
            <div className="page-header">
                <div className="header-content">
                    <h1>Employee Performance Review</h1>
                </div>

                <div className="action-buttons">
                    <WeeklyReminderPopup employees={employees || []} />
                    <MonthlyScoreEmailPopup employees={employees || []} />
                </div>
            </div>

            {error && (
                <div className="alert error">
                    <HiExclamationCircle className="icon" />
                    <p>{error}</p>
                </div>
            )}

            {reviewError && (
                <div className="alert error">
                    <HiExclamationCircle className="icon" />
                    <p>{reviewError}</p>
                </div>
            )}

            {reminderError && (
                <div className="alert error">
                    <HiExclamationCircle className="icon" />
                    <p>{reminderError}</p>
                </div>
            )}

            {reminderSuccess && (
                <div className="alert success">
                    <HiCheckCircle className="icon" />
                    <p>Reminder sent successfully to all employees!</p>
                </div>
            )}

            <div className="employee-selection">
                <h2>Select Employee</h2>
                <div className="select-container">
                    <select
                        className="employee-select"
                        value={selectedEmployee}
                        onChange={handleEmployeeChange}
                    >
                        <option value="">Choose an employee...</option>
                        {employees?.map((employee) => (
                            <option key={employee.email} value={employee.email}>
                                {employee.name} ({employee.email})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {selectedEmployee && (
                <div className="tabs-container">
                    <div className="tabs-header">
                        <div
                            className={`tab ${
                                activeTab === "reports" ? "active" : ""
                            }`}
                            onClick={() => handleTabSelect("reports")}
                        >
                            Weekly Reports
                        </div>
                        <div
                            className={`tab ${
                                activeTab === "overview" ? "active" : ""
                            }`}
                            onClick={() => handleTabSelect("overview")}
                        >
                            Score Overview
                        </div>
                    </div>

                    <div className="tab-content">
                        {activeTab === "reports" ? (
                            <div className="review-section">
                                <div
                                    className="reports-layout"
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "350px 1fr",
                                        gap: "20px",
                                    }}
                                >
                                    <ReportList
                                        reports={getEmployeeReportsList()}
                                        selectedReportId={selectedReportId}
                                        onSelectReport={handleReportSelect}
                                        isLoading={loading}
                                    />

                                    <ReviewForm
                                        report={selectedReport}
                                        onSubmitReview={handleReviewSubmit}
                                        isLoading={reviewLoading}
                                    />
                                </div>
                            </div>
                        ) : (
                            <MonthlyScores
                                scores={monthlyScores}
                                isLoading={loading}
                            />
                        )}
                    </div>
                </div>
            )}

            {!selectedEmployee && (
                <div className="review-section">
                    <div className="no-employee-selected">
                        <HiOutlineUserGroup className="icon" />
                        <h3>No Employee Selected</h3>
                        <p>
                            Please select an employee to view their performance
                            reports and monthly scores.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPerformanceReview;
